import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import useSEO from '../hooks/useSEO';
import RelatedSolutions from '../components/RelatedSolutions';
import CrossSellBlock from '../components/CrossSellBlock';

const { 
  FiZap, FiCpu, FiMessageSquare, FiTarget, FiCheckCircle, FiArrowRight, 
  FiInstagram, FiXCircle, FiTrendingUp, FiShield, FiArrowDown, 
  FiHeadphones, FiImage, FiCalendar, FiClock, FiMessageCircle 
} = FiIcons;

const AiLeadAgent = () => {
  const { t, lang } = useLanguage();
  const content = t.aiLeadAgent;
  const INSTAGRAM_URL = "https://www.instagram.com/mathiasmazeedeppe/";
  const TIDYCAL_URL = "https://tidycal.com/mathiasdeppe/free-15-minute-growth-review";

  // Updated icons mapping
  const whatIcons = [FiZap, FiCpu, FiMessageSquare, FiTarget, FiCheckCircle, FiMessageCircle];
  const capIcons = [FiMessageSquare, FiImage, FiCalendar, FiMessageCircle, FiInstagram];

  useSEO(
    "AI Lead Assistant for Instagram DMs | Mathias Deppe",
    "AI-powered Instagram lead qualification, Comment to DM automation and booking for businesses that already receive inbound enquiries."
  );

  // Updated pivot definitions with specific technical workflow
  const pivotUpdates = {
    en: {
      commentToDmTitle: "Comment to DM",
      commentToDmDesc: "Tell your audience to comment a trigger word. The AI instantly replies to their comment publicly and sends a DM to start the conversation.",
      commentToDmShort: "Comment to DM automation",
      ctaBody: "Book a free 15-minute Growth Review and we'll look at your current enquiry flow and identify where automation could save time, automate comment-to-DM or help more prospects reach the next step."
    },
    es: {
      commentToDmTitle: "De Comentario a DM",
      commentToDmDesc: "Pide a tu audiencia que comente una palabra clave. La IA responde al instante a su comentario públicamente y les envía un DM para iniciar la conversación.",
      commentToDmShort: "Automatización de Comentario a DM",
      ctaBody: "Reserva una Revisión de Crecimiento gratuita de 15 minutos. Analizaremos tu flujo actual de consultas e identificaremos dónde la automatización podría ahorrar tiempo, automatizar el paso de comentario a DM o ayudar a que más prospectos den el siguiente paso."
    }
  };

  const p = pivotUpdates[lang] || pivotUpdates.en;

  const relatedLinks = [
    { 
      path: "/beauty-clinic", 
      label: lang === 'es' ? "Soluciones para Clínicas" : "Clinic Solutions",
      description: lang === 'es' ? "Especializado para medicina estética y clínicas de belleza." : "Specialized for aesthetic medicine and beauty clinics."
    },
    { 
      path: "/fitness-offer", 
      label: lang === 'es' ? "Soluciones para Gimnasios" : "Gym Solutions",
      description: lang === 'es' ? "Inyección de efectivo y captación de leads para centros fitness." : "Cash injection and lead capture for fitness centers."
    }
  ];

  return (
    <div className="bg-zinc-950 text-white min-h-screen selection:bg-orange-500/30">
      {/* Hero Section */}
      <section className="relative pt-40 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-orange-500/10 blur-[120px] pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-7xl font-black tracking-tighter leading-[0.9] mb-6 uppercase"
          >
            {content.hero.h1}
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="text-xl md:text-3xl font-bold text-orange-500 mb-2 tracking-tight">
              {content.hero.h2}
            </h2>
            <p className="text-lg md:text-xl font-medium text-zinc-300 mb-12 tracking-tight opacity-80">
              {content.hero.subH2}
            </p>
          </motion.div>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="max-w-3xl mx-auto text-zinc-400 text-lg md:text-xl leading-relaxed mb-12"
          >
            {content.hero.body}
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col items-center gap-8"
          >
            <a href="#demo" className="inline-flex items-center gap-3 px-8 py-4 bg-white text-zinc-950 font-black rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105">
              {content.hero.cta}
            </a>
            <div className="w-full max-w-2xl p-6 bg-zinc-900/50 border border-orange-500/30 rounded-2xl backdrop-blur-sm relative group overflow-hidden">
              <div className="absolute inset-0 bg-orange-500/5 translate-x-full group-hover:translate-x-0 transition-transform duration-700" />
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="relative z-10 flex flex-col md:flex-row items-center justify-center gap-4 text-orange-500 font-bold">
                <SafeIcon icon={FiInstagram} className="text-2xl" />
                <span className="text-center">{content.hero.proof}</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What It Does Section */}
      <section className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter uppercase">
            {content.whatItDoes.h2}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {content.whatItDoes.items.map((item, i) => (
              <motion.div 
                key={i}
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-8 bg-zinc-950 border border-white/5 rounded-3xl hover:border-orange-500/20 transition-all group"
              >
                <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center mb-6 group-hover:bg-orange-500/10 transition-colors">
                  <SafeIcon icon={whatIcons[i]} className="text-2xl text-orange-500" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-white">
                  {i === 5 ? p.commentToDmTitle : item.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed text-sm">
                  {i === 5 ? p.commentToDmDesc : item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Flowchart */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter uppercase">
            {content.flow.h2}
          </h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-2">
            {content.flow.steps.map((step, i) => (
              <React.Fragment key={i}>
                <motion.div 
                  whileInView={{ opacity: 1, scale: 1 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex flex-col items-center"
                >
                  <div className={`px-6 py-4 rounded-xl font-black text-sm uppercase tracking-widest text-center min-w-[140px] ${i === content.flow.steps.length - 1 ? 'bg-orange-500 text-white' : 'bg-zinc-900 text-zinc-400 border border-white/5'}`}>
                    {step}
                  </div>
                  {i === content.flow.steps.length - 1 && (
                    <motion.div 
                      initial={{ opacity: 0, y: 5 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      className="mt-3 text-[10px] font-black uppercase tracking-widest text-orange-500 bg-orange-500/10 px-3 py-1 rounded-full"
                    >
                      {content.flow.teamHandover}
                    </motion.div>
                  )}
                </motion.div>
                {i < content.flow.steps.length - 1 && (
                  <div className="flex items-center justify-center py-2">
                    <SafeIcon icon={FiArrowRight} className="text-orange-500 text-xl hidden md:block" />
                    <SafeIcon icon={FiArrowDown} className="text-orange-500 text-xl md:hidden" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <p className="text-center mt-12 text-zinc-500 italic text-lg">
            {content.flow.subtext}
          </p>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 px-6 bg-zinc-900/20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter uppercase">
            {content.capabilities.h2}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {content.capabilities.items.map((item, i) => (
              <motion.div 
                key={i}
                whileInView={{ opacity: 1, x: 0 }}
                initial={{ opacity: 0, x: -20 }}
                viewport={{ once: true }}
                className="flex gap-6 items-start"
              >
                <div className="w-12 h-12 bg-zinc-900 rounded-full flex items-center justify-center shrink-0 border border-white/5">
                  <SafeIcon icon={capIcons[i]} className="text-xl text-orange-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {i === 3 ? p.commentToDmTitle : item.title}
                  </h3>
                  <p className="text-zinc-400 leading-relaxed text-sm">
                    {i === 3 ? p.commentToDmDesc : item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Difference (Positioning) */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            whileInView={{ opacity: 1, scale: 1 }}
            initial={{ opacity: 0, scale: 0.95 }}
            viewport={{ once: true }}
            className="p-12 md:p-16 bg-zinc-900 border-2 border-orange-500/30 rounded-[3rem] relative overflow-hidden text-center"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 blur-[100px] pointer-events-none" />
            <div className="relative z-10">
              <p className="text-xl md:text-2xl text-white font-bold leading-relaxed mb-12">
                {content.difference.text}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                {content.difference.logic.map((step, i) => (
                  <React.Fragment key={i}>
                    <span className="px-4 py-2 bg-zinc-950 border border-white/10 rounded-lg text-sm font-black uppercase tracking-widest text-orange-500">
                      {step}
                    </span>
                    {i < content.difference.logic.length - 1 && (
                      <SafeIcon icon={FiArrowRight} className="text-zinc-600" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who is it for? */}
      <section className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter uppercase">
            {content.whoIsItFor.h2}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {content.whoIsItFor.items.map((item, i) => (
              <div key={i} className={`p-8 bg-zinc-950 border rounded-2xl ${i === 0 ? 'border-orange-500/50 shadow-[0_0_30px_rgba(249,115,22,0.1)]' : 'border-white/5'}`}>
                <h3 className={`text-xl font-bold mb-3 ${i === 0 ? 'text-orange-500' : 'text-white'}`}>{item.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="max-w-3xl mx-auto p-8 bg-orange-500/10 border-l-4 border-orange-500 rounded-r-2xl">
            <p className="text-orange-500 font-bold text-lg">
              {content.whoIsItFor.disclaimer}
            </p>
          </div>
        </div>
      </section>

      {/* Sense vs Doesn't Sense Comparison */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="p-10 bg-zinc-900/50 border border-white/5 rounded-[2.5rem] relative group overflow-hidden">
              <div className="absolute top-0 right-0 p-6 opacity-20 group-hover:opacity-100 transition-opacity">
                <SafeIcon icon={FiCheckCircle} className="text-4xl text-orange-500" />
              </div>
              <h3 className="text-xl font-black text-white uppercase tracking-widest mb-8 pr-12">
                {content.comparison.sense.title}
              </h3>
              <ul className="space-y-6">
                {content.comparison.sense.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-4 text-zinc-300">
                    <SafeIcon icon={FiCheckCircle} className="text-orange-500 mt-1 shrink-0" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-10 bg-zinc-950 border border-white/5 rounded-[2.5rem] relative group overflow-hidden grayscale hover:grayscale-0 transition-all">
              <div className="absolute top-0 right-0 p-6 opacity-20 group-hover:opacity-100 transition-opacity">
                <SafeIcon icon={FiXCircle} className="text-4xl text-zinc-500" />
              </div>
              <h3 className="text-xl font-black text-zinc-500 uppercase tracking-widest mb-8 pr-12">
                {content.comparison.noSense.title}
              </h3>
              <ul className="space-y-6">
                {content.comparison.noSense.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-4 text-zinc-500">
                    <div className="w-1.5 h-1.5 bg-zinc-700 rounded-full mt-2.5 shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The Proof (Massive Highlight) */}
      <section id="demo" className="py-32 px-6 bg-zinc-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-orange-500/10 via-transparent to-transparent opacity-50" />
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div 
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 40 }}
            viewport={{ once: true }}
            className="bg-zinc-900/80 backdrop-blur-xl border-2 border-orange-500 p-12 md:p-24 rounded-[4rem] text-center shadow-[0_0_100px_rgba(249,115,22,0.15)]"
          >
            <h2 className="text-4xl md:text-7xl font-black mb-6 tracking-tighter uppercase text-white">
              {content.proof.h2}
            </h2>
            <p className="text-xl md:text-2xl text-zinc-400 mb-16 max-w-3xl mx-auto font-medium">
              {content.proof.subH2}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
              {content.proof.steps.map((step, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-14 h-14 bg-orange-500 text-white rounded-2xl flex items-center justify-center font-black text-2xl mb-6 transform -rotate-3 group-hover:rotate-0 transition-transform">
                    {i + 1}
                  </div>
                  <p className="text-sm font-black text-white uppercase tracking-widest leading-tight">
                    {step}
                  </p>
                </div>
              ))}
            </div>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-4 px-12 py-6 bg-white text-zinc-950 text-2xl font-black rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105 shadow-2xl group">
              {content.proof.cta}
              <SafeIcon icon={FiInstagram} className="group-hover:scale-110 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Cross-Sell Loop */}
      <CrossSellBlock variant="ai" />

      {/* Internal Linking */}
      <RelatedSolutions links={relatedLinks} />

      {/* Final CTA */}
      <section className="py-32 px-6 bg-zinc-950">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div 
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-black text-white mb-8 tracking-tighter">
              {content.finalCta.h2}
            </h2>
            <p className="text-xl text-zinc-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              {p.ctaBody}
            </p>
            <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="px-10 py-5 text-xl font-black text-zinc-950 bg-white rounded-full hover:bg-orange-500 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-2xl">
              {content.finalCta.cta}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AiLeadAgent;